import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';
import { trackCheckoutClick } from './MetaPixel';
import type { ProductKey } from './MetaPixel';
export const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 16
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.3,
  ease: [0.23, 1, 0.32, 1],
  delay
}} className={className} data-id="element-373" data-mp="184464ghkxlmt">
    {children}
  </motion.div>;
interface ButtonProps {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
  className?: string;
  /** Fires Meta Pixel `InitiateCheckout` right before navigating. */
  trackingProduct?: ProductKey;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}
export const CTAButton = ({
  href,
  children,
  variant = 'primary',
  fullWidth = false,
  className = '',
  trackingProduct,
  onClick
}: ButtonProps) => {
  const isExternal = href.startsWith('http');
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Drop focus so the button never stays "lit" when coming back.
    e.currentTarget.blur();
    if (onClick) onClick(e);
    if (trackingProduct) trackCheckoutClick(e, href, trackingProduct);
  };
  return <a href={href} onClick={handleClick} rel={isExternal ? 'noopener' : undefined} className={cn('group/cta inline-flex items-center justify-center gap-2 rounded-full text-center transition-[transform,background-color,box-shadow] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 active:scale-[0.98]', fullWidth && 'w-full', variant === 'primary' ? 'bg-blue-600 px-8 py-4 text-[15px] font-bold uppercase tracking-wide text-white shadow-[0_10px_30px_-10px_rgba(37,99,235,0.8)] animate-glow-button [@media(hover:hover)]:hover:-translate-y-0.5 [@media(hover:hover)]:hover:bg-blue-500 md:text-base' : 'border border-white/15 bg-white/5 px-7 py-3.5 text-base font-semibold text-white [@media(hover:hover)]:hover:bg-white/10', className)} data-id="element-374" data-mp="2ql563277hfy4">
      {children}
    </a>;
};
export const Section = ({
  children,
  className = '',
  id
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={cn('overflow-hidden px-5 py-16 md:px-8 md:py-24', className)} style={{
  paddingTop: "29px"
}} data-id="element-375" data-mp="2gxjdkqchvj2c">
    <div className="mx-auto max-w-6xl" data-id="element-376" data-mp="2296dbh6dje21">{children}</div>
  </section>;
export const Accent = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="text-sky-400" data-id="element-377" data-mp="275wvqb5nilqm">{children}</span>;
export const SectionTitle = ({
  children,
  sub,
  className = ''
}: {
  children: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
}) => <div className={cn('mb-10 text-center md:mb-14', className)} data-id="element-378" data-mp="2zq3sf3vcznn5">
    <h2 className="font-display text-[32px] font-black uppercase italic leading-[1.05] tracking-tight text-white md:text-6xl" style={{
    fontSize: "28px"
  }} data-id="element-379" data-mp="o80bzzgk2ikl">
      {children}
    </h2>
    {sub && <p className="mx-auto mt-4 max-w-2xl text-base text-slate-400 md:text-lg" data-id="element-380" data-mp="xxtm0w11upmq">
        {sub}
      </p>}
  </div>;
export const Stars = ({
  size = 12
}: {
  size?: number;
}) => <div className="flex text-amber-400" role="img" aria-label="5 de 5 estrellas" data-id="element-381" data-mp="59nzq4ucksks">
    {[0, 1, 2, 3, 4].map(j => <svg key={j} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-382" data-mp="c4lh4xngfsu">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-383" data-mp="a9ag2n5h2oxt" />
      </svg>)}
  </div>;
export const pad2 = (n: number) => n.toString().padStart(2, '0');